import React from "react";
import { Button } from "./ui/button";
import { ToggleGroup, ToggleGroupItem } from "./ui/toggle-group";
import {
  Radar,
  TrainFront,
  Layers,
  Clock,
  Package,
  Weight,
  ShieldCheck,
  HardHat,
  RotateCcw,
  Boxes,
  AlertTriangle,
} from "lucide-react";
import { stationName } from "../i18n";
import { LangSwitcher } from "./LangSwitcher";
import { siteUrl, bookingUrl } from "../lib/siteUrl";

const LegendDot = ({ color, label, dashed }) => (
  <div className="flex items-center gap-2">
    <span
      className="w-3.5 h-3.5 rounded-[2px] shrink-0"
      style={{
        background: dashed ? "transparent" : color,
        border: dashed ? "1.5px dashed rgba(255,255,255,0.5)" : "none",
        boxShadow: dashed ? "none" : `0 0 6px ${color}`,
      }}
    />
    <span className="text-[11px] text-zinc-400">{label}</span>
  </div>
);

const LegendMarker = ({ type, label }) => (
  <div className="flex items-center gap-2">
    {type === "ocr" ? (
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400 ring-1 ring-black shrink-0" />
    ) : type === "bogie" ? (
      <span className="w-0 h-0 border-l-[5px] border-r-[5px] border-b-[8px] border-l-transparent border-r-transparent border-b-orange-400 shrink-0" />
    ) : (
      <span className="w-2.5 h-2.5 rotate-45 bg-blue-300 ring-1 ring-black shrink-0" />
    )}
    <span className="text-[11px] text-zinc-400">{label}</span>
  </div>
);

export const Sidebar = ({
  yard,
  lang,
  setLang,
  role,
  setRole,
  mode,
  setMode,
  view,
  setView,
  setSelectedWagon,
  t,
  onReset,
}) => {
  const stations = yard?.stations || [];
  const totals = yard?.totals || {};

  const modeIcons = { dwell: Clock, cargo: Package, weight: Weight };

  const legends = {
    dwell: [
      { c: "#10b981", l: t("short") },
      { c: "#f59e0b", l: t("medium") },
      { c: "#f43f5e", l: t("long") },
    ],
    cargo: [
      { c: "#52525b", l: t("coal") },
      { c: "#fff", l: t("empty"), dashed: true },
      { c: "#2563eb", l: t("foreign") },
    ],
    weight: [
      { c: "#22d3ee", l: t("light") },
      { c: "#3b82f6", l: t("weightRange3070") },
      { c: "#6366f1", l: t("weightRange7094") },
      { c: "#e11d48", l: t("overweight") },
    ],
  };

  return (
    <aside className="h-full flex flex-col bg-[#0d0d10] border-r border-zinc-800 overflow-y-auto no-scrollbar">
      {/* brand */}
      <div className="p-4 border-b border-zinc-800">
        <div className="flex items-center gap-2.5">
          <div className="grid place-items-center w-9 h-9 rounded-md bg-sky-500/15 text-sky-400">
            <Radar size={20} />
          </div>
          <div>
            <h1
              className="text-[15px] font-bold leading-none tracking-tight text-white"
              style={{ fontFamily: "Chivo, sans-serif" }}
            >
              {t("appTitle")}
            </h1>
            <p className="text-[10px] text-zinc-500 mt-1 leading-tight">{t("appSub")}</p>
          </div>
        </div>
        <div className="mt-3 flex items-center gap-1.5">
          <span className="flex items-center gap-1 text-[9px] font-mono text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-sm tracking-widest">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            {t("liveMode")}
          </span>
        </div>
      </div>

      {/* role + lang */}
      <div className="p-4 border-b border-zinc-800 space-y-3">
        <div>
          <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
            {t("role")}
          </label>
          <div className="mt-1.5 grid grid-cols-2 gap-1.5">
            <button
              data-testid="role-dispatcher"
              onClick={() => setRole("dispatcher")}
              className={`flex flex-col items-center justify-center gap-1 py-2 rounded-sm text-[10px] font-medium transition-all ${
                role === "dispatcher"
                  ? "bg-sky-500 text-white shadow-[0_0_12px_rgba(56,189,248,0.4)]"
                  : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <ShieldCheck size={14} /> {t("dispatcher")}
            </button>
            <button
              data-testid="role-worker"
              onClick={() => setRole("worker")}
              className={`flex flex-col items-center justify-center gap-1 py-2 rounded-sm text-[10px] font-medium transition-all ${
                role === "worker"
                  ? "bg-amber-500 text-black shadow-[0_0_12px_rgba(245,158,11,0.4)]"
                  : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <HardHat size={14} /> {t("worker")}
            </button>
          </div>
        </div>
        <div>
          <LangSwitcher lang={lang} setLang={setLang} />
        </div>
      </div>

      {/* stations nav */}
      <div className="p-4 border-b border-zinc-800">
        <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {t("stations")}
        </label>
        <button
          data-testid="nav-overview"
          onClick={() => {
            setView("overview");
            setSelectedWagon(null);
          }}
          className={`mt-2 w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-[12px] transition-all ${
            view === "overview"
              ? "bg-zinc-800 text-white"
              : "text-zinc-400 hover:bg-zinc-900"
          }`}
        >
          <Boxes size={14} /> {t("overview")}
        </button>
        <div className="mt-1.5 space-y-1">
          {stations.map((s) => (
            <button
              key={s.id}
              data-testid={`nav-station-${s.code}`}
              onClick={() => {
                setView(s.id);
                setSelectedWagon(null);
              }}
              className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-sm text-[12px] transition-all ${
                view === s.id ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900"
              }`}
            >
              {s.type === "sorting" ? <Layers size={14} /> : <TrainFront size={13} />}
              <span className="font-mono text-[10px] text-zinc-500 w-10">{s.code}</span>
              <span className="flex-1 truncate text-left">
                {stationName(s.code, lang).replace(/[«»"]/g, "")}
              </span>
              {s.alert && <AlertTriangle size={12} className="text-rose-400 animate-pulse" />}
              <span className="font-mono text-[10px] text-zinc-600">{s.wagon_count}</span>
            </button>
          ))}
        </div>
      </div>

      {/* highlight mode */}
      <div className="p-4 border-b border-zinc-800">
        <label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">
          {t("highlight")}
        </label>
        <ToggleGroup
          type="single"
          value={mode}
          onValueChange={(v) => v && setMode(v)}
          className="mt-2 grid grid-cols-3 gap-1.5"
        >
          {["dwell", "cargo", "weight"].map((m) => {
            const Icon = modeIcons[m];
            return (
              <ToggleGroupItem
                key={m}
                value={m}
                data-testid={`mode-${m}`}
                className="flex-col gap-1 h-auto py-2 data-[state=on]:bg-sky-500 data-[state=on]:text-white bg-zinc-900 text-zinc-400 rounded-sm border-0"
              >
                <Icon size={15} />
                <span className="text-[9px]">{t(m)}</span>
              </ToggleGroupItem>
            );
          })}
        </ToggleGroup>

        {/* legend */}
        <div className="mt-3 space-y-1.5">
          <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">
            {t("legend")}
          </span>
          {legends[mode].map((it, i) => (
            <LegendDot key={i} color={it.c} label={it.l} dashed={it.dashed} />
          ))}
        </div>

        {/* wagon markers */}
        <div className="mt-3 space-y-1.5">
          <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-600">
            {t("legendMarkers")}
          </span>
          <LegendMarker type="ocr" label={t("markerOcrCheck")} />
          <LegendMarker type="foreign" label={t("markerForeign")} />
          <LegendMarker type="bogie" label={t("markerBogieSkew")} />
        </div>
      </div>

      {/* totals */}
      <div className="p-4 grid grid-cols-2 gap-2 mt-auto">
        <Stat label={t("totalWagons")} value={totals.wagons || 0} />
        <Stat label={t("coal")} value={totals.coal || 0} color="#a1a1aa" />
        <Stat label={t("empty")} value={totals.empty || 0} color="#71717a" />
        <Stat
          label={t("alerts")}
          value={totals.alerts || 0}
          color={totals.alerts ? "#f43f5e" : "#10b981"}
        />
      </div>

      <div className="p-4 pt-0 space-y-2">
        <Button
          data-testid="reset-btn"
          onClick={onReset}
          variant="outline"
          className="w-full h-8 text-[11px] gap-1.5 bg-transparent border-zinc-800 text-zinc-500 hover:text-white hover:bg-zinc-900 rounded-sm"
        >
          <RotateCcw size={12} /> {t("reset")}
        </Button>
        <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 text-center">
          {t("demoBadge")}
        </p>
        <div className="flex flex-col items-center gap-1 text-[11px]">
          <a
            href={siteUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-site"
            className="text-sky-400 hover:text-sky-300"
          >
            {t("ctaSite")}
          </a>
          <a
            href={bookingUrl(lang)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-booking"
            className="text-zinc-500 hover:text-zinc-300"
          >
            {t("ctaBooking")}
          </a>
        </div>
      </div>
    </aside>
  );
};

const Stat = ({ label, value, color = "#38bdf8" }) => (
  <div className="bg-zinc-900/60 rounded-sm px-2.5 py-2 border border-zinc-800">
    <div className="text-[9px] uppercase tracking-widest text-zinc-500 truncate">{label}</div>
    <div className="text-lg font-bold font-mono leading-none mt-1" style={{ color }}>
      {value}
    </div>
  </div>
);
